import { ColumnFilter, LookupTableMemberDefinition } from '../../generated-service-lib';
import { GeneratedLookupService } from '../generatedServices';
import { assertSuccessfulResponse, getValidResponseDataOrEmptyArray, getValidResponseDataOrThrow } from '../helpers';
import { KeyValuePair, LookupTable, LookupTableArrayView } from '../types';

export class LookupService {
  readonly unvalidatedClient: GeneratedLookupService;
  constructor(domain?: string) {
    this.unvalidatedClient = new GeneratedLookupService(domain);
  }

  private getSafeMemberDefinition = (memberDefinition: LookupTableMemberDefinition) => ({
    name: memberDefinition?.name || '',
    dataType: memberDefinition?.dataType || '',
    defaultSearchColumn: memberDefinition?.defaultSearchColumn,
  });

  private convertLookupRowArrayToObject = (lookupRow: KeyValuePair<string, unknown>[]) =>
    lookupRow.reduce(
      (rowObject, currentCell) => ({
        ...rowObject,
        [currentCell.key]: currentCell.value,
      }),
      {}
    );

  private convertLookupRowObjectToArray = (lookupRow: { [key: string]: string }): KeyValuePair<string, string>[] =>
    Object.entries(lookupRow).map(([key, value]) => ({ key, value }));

  getTableDefinitions = async (clientId: string, tableName: string) => {
    return await this.unvalidatedClient.getTablesByTableName({ clientId, tableName }).then(getValidResponseDataOrThrow);
  };

  getTableMembersActiveVersion = async (clientId: string, tableName: string) => {
    return await this.unvalidatedClient
      .getTablesByTableNameMembers({ clientId, tableName })
      .then(getValidResponseDataOrThrow)
      .then((response) => response.lookupValues)
      // next line doesn't really do anything except get rid of possible undefined values to satisfy type-checking
      .then(
        (tableMembers) =>
          tableMembers?.map((l: any[]) => l.map((v) => ({ key: v.key || '', value: v.value || '' }))) || []
      )
      .then((tableMembers) => tableMembers?.map((l: any[]) => l.filter((v) => v.key !== 'ETag')))
      .then((tableMembers) => tableMembers.map(this.convertLookupRowArrayToObject));
  };

  getTableMembers = async (clientId: string, tableName: string, versionNumber: number) => {
    return await this.unvalidatedClient
      .getTablesByTableNameVersionsByVersionNumberMembers({
        clientId,
        tableName,
        versionNumber,
      })
      .then(getValidResponseDataOrThrow)
      .then((response) => response.lookupValues)
      // next line doesn't really do anything except get rid of possible undefined values to satisfy type-checking
      .then(
        (tableMembers) =>
          tableMembers?.map((l: any[]) => l.map((v) => ({ key: v.key || '', value: v.value || '' }))) || []
      )
      .then((tableMembers) => tableMembers?.map((l: any[]) => l.filter((v) => v.key !== 'ETag')))
      .then((tableMembers) => tableMembers.map(this.convertLookupRowArrayToObject));
  };

  getTableMembersWithFilters = async (
    clientId: string,
    tableName: string,
    versionNumber: number,
    filters: ColumnFilter[]
  ) => {
    return await this.unvalidatedClient
      .postTablesByTableIdFilteredandsearch({
        clientId,
        tableId: tableName,
        body: filters,
      })
      .then(getValidResponseDataOrEmptyArray)
      // next line doesn't really do anything except get rid of possible undefined values to satisfy type-checking
      .then(
        (tableMembers) =>
          tableMembers.map((l: any[]) => l.map((v) => ({ key: v.key || '', value: v.value || '' }))) || []
      )
      .then((tableMembers) => tableMembers?.map((l: any[]) => l.filter((v) => v.key !== 'ETag')))
      .then((tableMembers) => tableMembers.map(this.convertLookupRowArrayToObject))
      .catch((err) => {
        if (err.status === 404) return [];
        throw err;
      });
  };

  getAllTables = async (clientId: string): Promise<LookupTableArrayView[]> => {
    const response = await this.unvalidatedClient.getTables({ clientId });
    const lookupTables = getValidResponseDataOrThrow(response);
    return lookupTables.map((table: any) => ({
      id: table?.id || '',
      name: table?.name || '',
      definitions: table?.definitions || [],
    }));
  };

  createTable = async (clientId: string, table: LookupTable): Promise<LookupTable> => {
    await this.unvalidatedClient
      .postTables({
        clientId,
        body: {
          ...table,
          lookupValues: table.lookupValues.map(this.convertLookupRowObjectToArray),
        },
      })
      .then(getValidResponseDataOrThrow);

    return table;
  };

  updateActiveTableVersion = async (clientId: string, tableName: string, versionToSetActive: number) => {
    return await this.unvalidatedClient
      .patchTablesByTableName({
        clientId,
        tableName,
        body: {
          activeVersionNumber: versionToSetActive,
        },
      })
      .then(assertSuccessfulResponse);
  };

  patchMembersOnTableVersion = async (
    clientId: string,
    tableId: string,
    versionNumber: number,
    tableMembers: { [key: string]: string }[]
  ) => {
    return await this.unvalidatedClient
      .patchTablesByTableNameVersionsByVersionNumber({
        clientId,
        tableName: tableId,
        versionNumber,
        body: tableMembers.map((tableMember) => Object.entries(tableMember).map(([key, value]) => ({ key, value }))),
      })
      .then(assertSuccessfulResponse);
  };

  deleteMembersOnTableVersion = async (
    clientId: string,
    tableId: string,
    versionNumber: number,
    tableMembers: { [key: string]: string }[]
  ) => {
    return await this.unvalidatedClient
      .deleteTablesByTableNameVersionsByVersionNumberMembers({
        clientId,
        tableName: tableId,
        versionNumber,
        body: tableMembers.map((tableMember) => Object.entries(tableMember).map(([key, value]) => ({ key, value }))),
      })
      .then(getValidResponseDataOrThrow);
  };

  deleteTableVersion = async (
    clientId: string,
    tableId: string,
    versionNumber: number,
    dangerousAllowDeletionOfActiveVersion: boolean = false
  ) => {
    return await this.unvalidatedClient
      .deleteTablesByTableNameVersionsByVersionNumber({
        clientId,
        tableName: tableId,
        versionNumber,
        overrideActiveFlag: dangerousAllowDeletionOfActiveVersion,
      })
      .then(assertSuccessfulResponse);
  };

  createNewVersionOfTable = async (
    clientId: string,
    tableId: string,
    memberDefinitions: LookupTableMemberDefinition[],
    idColumns: string[] = []
  ) => {
    return await this.unvalidatedClient
      .putTablesByTableName({
        clientId,
        tableName: tableId,
        body: { memberDefinitions, idColumns },
      })
      .then(getValidResponseDataOrThrow);
  };
}
